import React, { useState, useRef, useEffect } from "react";
import BannerPreview from "./BannerPreview";
import ImageCropperModal from "./ImageCropperModal";
import Pagination from "@/components/Pagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowUp,
  faTrash,
  faImage,
  faPalette,
  faHeading,
  faMousePointer,
  faLayerGroup,
  faSearch,
  faRotateRight,
  faEdit,
  faCropSimple
} from "@fortawesome/free-solid-svg-icons";
import api from "@/auth/api";
import { alertError, alertSuccess } from "@/components/Alert";

const Section = ({ icon, title, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4 mb-4 transition-all hover:shadow-md">
    <div className="flex items-center gap-3 pb-2 border-b border-gray-50">
      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
        <FontAwesomeIcon icon={icon} />
      </div>
      <h3 className="font-bold text-gray-800 tracking-tight">{title}</h3>
    </div>
    <div className="space-y-4 pt-2">
      {children}
    </div>
  </div>
);

const InputField = ({ label, value, onChange, placeholder, type = "text" }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const CheckboxField = ({ label, checked, onChange }) => (
  <div className="flex items-center gap-2 py-1">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
    />
    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => onChange(!checked)}>{label}</label>
  </div>
);

const ColorField = ({ label, value, onChange }) => (
  <div className="flex items-center justify-between py-1">
    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</label>
    <div className="flex items-center gap-2 bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-200">
      <span className="text-[10px] font-mono text-gray-400 uppercase">{value}</span>
      <div className="relative w-6 h-6 rounded-md overflow-hidden border border-gray-300 shadow-inner">
        <input
          type="color"
          value={value}
          className="absolute -inset-1 w-10 h-10 cursor-pointer"
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  </div>
);

const BannerCMS = () => {
  const [form, setForm] = useState({
    title: "",
    text: "",
    image: "",
    text_color: "#000000",
    text_x: 50,
    text_y: 35,
    button_text: "",
    button_link: "",
    button_bg_color: "#000000",
    button_text_color: "#ffffff",
    button_x: 50,
    button_y: 85,
    background_color: "#f8fafc",
    is_url: false,
  });

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const [cropperModalOpen, setCropperModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState("");
  const [rawFileName, setRawFileName] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const fileInputRef = useRef(null);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRawFileName(file.name);
      const imageUrl = URL.createObjectURL(file);
      setRawImageSrc(imageUrl);
      setCropperModalOpen(true);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCropComplete = (croppedFile, croppedImageUrl) => {
    setImageFile(croppedFile);
    handleChange("image", croppedImageUrl);
    setCropperModalOpen(false);
    setRawImageSrc("");
  };

  const handleCropCancel = () => {
    setCropperModalOpen(false);
    setRawImageSrc("");
  };

  const handleReCrop = () => {
    if (form.image) {
      setRawFileName("banner_image.jpg");
      setRawImageSrc(form.image);
      setCropperModalOpen(true);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    handleChange("image", "");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCancelEdit = () => {
    setForm({
      title: "",
      text: "",
      image: "",
      text_color: "#000000",
      text_x: 50,
      text_y: 35,
      button_text: "",
      button_link: "",
      button_bg_color: "#000000",
      button_text_color: "#ffffff",
      button_x: 50,
      button_y: 85,
      background_color: "#f8fafc",
      is_url: false,
    });
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const saveBannerDesign = async () => {
    // all should be required
    if (!form.title || !form.text || !form.text_color || !form.background_color) {
      alertError("Please fill all the required fields!");
      return;
    }
    try {
      const url = '/surveyHistory/panel/notification/banner/template';
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("text", form.text);
      if (imageFile) {
        formData.append("image", imageFile);
      }
      formData.append("text_color", form.text_color);
      formData.append("text_x", form.text_x !== undefined ? form.text_x : 50);
      formData.append("text_y", form.text_y !== undefined ? form.text_y : 35);
      formData.append("button_text", form.button_text);
      formData.append("button_link", form.button_link);
      formData.append("button_bg_color", form.button_bg_color);
      formData.append("button_text_color", form.button_text_color);
      formData.append("button_x", form.button_x !== undefined ? form.button_x : 50);
      formData.append("button_y", form.button_y !== undefined ? form.button_y : 85);
      formData.append("background_color", form.background_color);
      formData.append("is_url", form.is_url ? "true" : "false");

      let resp;
      if (form.id) {
        formData.append("id", form.id);
        resp = await api.patch(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          }
        });
      } else {
        resp = await api.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          }
        });
      }

      console.log(resp)
      alertSuccess(`Banner Design ${form.id ? 'Updated' : 'Saved'} Successfully!`);
      setCurrentPage(1);
      fetchBanners(debouncedSearch, 1);
      
      // Reset form after successful update
      if (form.id) {
        handleCancelEdit();
      }
    } catch (err) {
      console.log(err);
      alertError("Error saving Banner Design!");
    }
  }

  const fetchBanners = async (query = "", page = currentPage, size = pageSize) => {
    setLoading(true);
    try {
      const url = `/surveyHistory/panel/notification/banner/template?search=${query}&page=${page}&page_size=${size}`;
      const resp = await api.get(url);
      // Ensure it's an array, handle potential nested data
      // console.log(resp)
      const data = Array.isArray(resp.data) ? resp.data : (resp.data?.result || resp.data?.data || []);
      // console.log(data)y
      setBanners(data);

      let total_pages = 1;
      if (resp.data?.pagination?.count) total_pages = Math.ceil(resp.data.pagination.count / size);
      else if (resp.data?.total_pages) total_pages = resp.data.total_pages;
      else if (resp.data?.totalPages) total_pages = resp.data.totalPages;
      else if (resp.data?.total) total_pages = Math.ceil(resp.data.total / size);
      else if (resp.data?.count) total_pages = Math.ceil(resp.data.count / size);
      
      setTotalPages(total_pages);
    } catch (err) {
      console.error("Error fetching banners:", err);
      // alertError("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    fetchBanners(debouncedSearch, currentPage, pageSize);
  }, [debouncedSearch, currentPage, pageSize]);

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const onSearchSubmit = (e) => {
    if (e.key === "Enter") {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }
  };

  const loadTemplate = (template) => {
    setImageFile(null); // Reset file state when loading existing template
    setForm({
      ...template,
      // Handle potential image format difference
      image: template.image || ""
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-8 w-full mx-auto p-4 animate-in fade-in duration-700">
        {/* LEFT: Preview - Sticky */}
        <div className="lg:w-3/5 xl:w-2/3">
          <div className="sticky top-4 space-y-6">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-2xl font-black text-gray-900 tracking-tight">Banner Preview</h2>
                <p className="text-sm text-gray-500">Real-time visualization • Drag button to reposition</p>
              </div>
              <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold animate-pulse">
                LIVE
              </div>
            </div>

            <div className="rounded-2xl shadow-2xl shadow-blue-900/10 border border-gray-200 overflow-hidden bg-white p-2 max-w-[300px] sm:max-w-sm md:max-w-md lg:max-w-lg mx-auto">
              <BannerPreview
                data={form}
                onPositionChange={(coords) => setForm((prev) => ({ ...prev, ...coords }))}
              />
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-4 items-start">
              <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <FontAwesomeIcon icon={faLayerGroup} className="text-sm" />
              </div>
              <div>
                <h4 className="font-bold text-blue-900 text-sm">Design Tip</h4>
                <p className="text-xs text-blue-700 leading-relaxed mt-1">
                  Use high-contrast colors for text and buttons to ensure accessibility.
                  If using a background image, center the most important visual element.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: CMS FORM */}
        <div className="lg:w-2/5 xl:w-1/3 h-[calc(100vh-120px)] overflow-y-auto pr-2 custom-scrollbar">
          <div className="space-y-2">

            {/* CONTENT SECTION */}
            <Section icon={faHeading} title="Content Settings">
              <InputField
                label="Banner Title"
                value={form.title}
                placeholder="Enter eye-catching title..."
                onChange={(v) => handleChange("title", v)}
              />
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Banner Text</label>
                <textarea
                  value={form.text}
                  rows={3}
                  placeholder="Write a brief description..."
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                  onChange={(e) => handleChange("text", e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <ColorField
                  label="Text Color"
                  value={form.text_color}
                  onChange={(v) => handleChange("text_color", v)}
                />
              </div>
            </Section>

            {/* VISUALS SECTION */}
            <Section icon={faImage} title="Media & Style">
              <div className="space-y-4">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block">Background Media</label>

                {!form.image ? (
                  <div
                    onClick={() => fileInputRef.current.click()}
                    className="group border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 group-hover:bg-blue-100 group-hover:text-blue-500 transition-colors">
                      <FontAwesomeIcon icon={faCloudArrowUp} size="lg" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-gray-700">Click to upload</p>
                      <p className="text-xs text-gray-400 mt-1">SVG, PNG, JPG (max. 5MB)</p>
                      <p className="text-[11px] font-semibold text-blue-600 mt-1.5 bg-blue-50 px-2.5 py-1 rounded-full inline-block">
                        Auto-cropped to 500 × 200 px
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="relative group rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                    <img src={form.image} alt="Preview" className="w-full h-32 object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={handleReCrop}
                        className="p-2 bg-white rounded-full text-blue-600 hover:scale-110 transition-transform"
                        title="Crop Image (500 × 200)"
                      >
                        <FontAwesomeIcon icon={faCropSimple} />
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current.click()}
                        className="p-2 bg-white rounded-full text-blue-600 hover:scale-110 transition-transform"
                        title="Change Image"
                      >
                        <FontAwesomeIcon icon={faCloudArrowUp} />
                      </button>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="p-2 bg-white rounded-full text-red-600 hover:scale-110 transition-transform"
                        title="Remove Image"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                )}

                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleImageUpload}
                />

                <ColorField
                  label="Fallback Color"
                  value={form.background_color}
                  onChange={(v) => handleChange("background_color", v)}
                />
              </div>
            </Section>

            {/* ACTION SECTION */}
            <Section icon={faMousePointer} title="Call to Action">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <InputField
                  label="Button Label"
                  value={form.button_text}
                  placeholder="e.g. Shop Now"
                  onChange={(v) => handleChange("button_text", v)}
                />
                <InputField
                  label="Link URL"
                  value={form.button_link}
                  placeholder="https://..."
                  onChange={(v) => handleChange("button_link", v)}
                />
              </div>
              <div className="px-1 mb-2">
                <CheckboxField
                  label="Is URL"
                  checked={form.is_url}
                  onChange={(v) => handleChange("is_url", v)}
                />
              </div>

              <div className="flex flex-col gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="grid grid-cols-2 gap-4">
                  <ColorField
                    label="Button BG"
                    value={form.button_bg_color}
                    onChange={(v) => handleChange("button_bg_color", v)}
                  />
                  <ColorField
                    label="Button Text"
                    value={form.button_text_color}
                    onChange={(v) => handleChange("button_text_color", v)}
                  />
                </div>
              </div>
            </Section>

            {form.id ? (
              <div className="flex gap-4 mt-6 mb-12">
                <button className="flex-1 py-4 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl font-bold transition-all duration-200 border border-red-200" onClick={handleCancelEdit}>
                  Cancel
                </button>
                <button className="flex-1 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-xl font-bold shadow-lg shadow-blue-200 hover:shadow-blue-300 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200" onClick={saveBannerDesign}>
                  Update Banner
                </button>
              </div>
            ) : (
              <button className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-xl font-bold shadow-lg shadow-blue-200 hover:shadow-blue-300 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 mt-6 mb-12" onClick={saveBannerDesign}>
                Save Banner Design
              </button>
            )}
          </div>
        </div>
      </div>

      {/* SAVED TEMPLATES SECTION */}
      <div className="mt-12 w-full max-w-7xl mx-auto p-4 border-t border-gray-100 pt-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tight">Saved Banners</h2>
            <p className="text-sm text-gray-500 mt-1">Manage and reuse your previous banner designs</p>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
              />
              <input
                type="text"
                placeholder="Search templates..."
                value={search}
                onChange={handleSearch}
                onKeyDown={onSearchSubmit}
                className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 w-64 transition-all"
              />
            </div>
            <button
              onClick={() => fetchBanners(debouncedSearch, currentPage, pageSize)}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all"
              title="Refresh"
            >
              <FontAwesomeIcon icon={faRotateRight} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="text-sm text-gray-400 font-medium">Loading templates...</p>
          </div>
        ) : banners.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {banners.map((item) => (
                <div
                  key={item.id}
                  className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  <div className="h-32 sm:h-40 bg-gray-50 overflow-hidden relative">
                    <div className="scale-50 origin-top-left w-[200%] h-[200%]">
                      <BannerPreview data={item} />
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all pointer-events-none" />
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div className="overflow-hidden">
                      <h4 className="font-bold text-gray-800 truncate">{item.title || "Untitled Banner"}</h4>
                      <p className="text-xs text-gray-400 mt-0.5">ID: {item.id}</p>
                    </div>
                    <button
                      onClick={() => loadTemplate(item)}
                      className="p-2.5 bg-blue-50 text-blue-600 rounded-xl opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all hover:bg-blue-600 hover:text-white"
                      title="Edit Template"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-col lg:flex-row items-center justify-between gap-4 bg-gray-50/50 p-4 rounded-xl border border-gray-100">
              <div className="flex flex-wrap items-center gap-6">
                {/* Page Size Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 font-medium">Items per page:</span>
                  <select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className="bg-white border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block px-2.5 py-1.5 font-medium shadow-sm cursor-pointer outline-none transition-all hover:border-gray-300"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                
                {/* Jump to Page Selector */}
                {totalPages > 0 && (
                  <div className="flex items-center gap-2 border-l border-gray-200 pl-6">
                    <span className="text-sm text-gray-500 font-medium">Go to page:</span>
                    <select
                      value={currentPage}
                      onChange={(e) => setCurrentPage(parseInt(e.target.value))}
                      className="bg-white border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block px-2.5 py-1.5 font-medium shadow-sm cursor-pointer outline-none transition-all hover:border-gray-300"
                    >
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <option key={page} value={page}>
                          {page}
                        </option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-500 font-medium ml-1">of {totalPages}</span>
                  </div>
                )}
              </div>
              
              {totalPages > 1 && (
                <div className="flex-1 flex justify-end">
                  <Pagination
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalPages={totalPages}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200 py-20 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center text-gray-300 mb-4">
              <FontAwesomeIcon icon={faLayerGroup} size="xl" />
            </div>
            <h3 className="font-bold text-gray-800">No Templates Found</h3>
            <p className="text-sm text-gray-400 max-w-xs mt-2">
              {search ? `We couldn't find any templates matching "${search}"` : "Start creating your first banner design to see it here."}
            </p>
            {search && (
              <button
                onClick={() => { setSearch(""); setDebouncedSearch(""); setCurrentPage(1); fetchBanners("", 1, pageSize); }}
                className="mt-6 text-sm font-bold text-blue-600 hover:underline"
              >
                Clear Search
              </button>
            )}
          </div>
        )}
      </div>

      <ImageCropperModal
        isOpen={cropperModalOpen}
        imageSrc={rawImageSrc}
        fileName={rawFileName}
        onCropComplete={handleCropComplete}
        onCancel={handleCropCancel}
      />
    </>
  );
};

export default BannerCMS;